import { createRootRouteWithContext, HeadContent, Outlet, ScrollRestoration } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { TooltipProvider, ToastViewport } from "@/components/ui";
import { AuthModalProvider } from "@/components/auth/AuthModalProvider";
import { LanguageProvider } from "@/components/i18n/LanguageProvider";
import { buildHead } from "@/lib/seo";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Site-wide SEO + Open Graph fallback (SM-381). Every route below
  // overrides these with its own; a route that doesn't still previews as
  // the active brand rather than as an untitled page. The same defaults are
  // duplicated statically in index.html, which is what non-JS crawlers
  // read — see src/lib/seo.ts for the full split.
  //
  // canonical: false — only the matched leaf route knows the canonical
  // path, and TanStack doesn't dedupe links, so a canonical here would
  // sit in front of the real one on every page. See MetaInput.canonical.
  head: () => buildHead({ canonical: false }),
  component: RootLayout,
});

// Global providers live here so every route (app + dev) shares one tooltip
// layer, one auth modal, one toast viewport, and one active-language owner
// (LanguageProvider — boot order + catalog invalidation on switch, SM-286).
// The app chrome is in _app.
function RootLayout() {
  return (
    <LanguageProvider>
      <TooltipProvider delayDuration={200}>
        <AuthModalProvider>
          {/* Renders the active route's `head` descriptor (title, description,
              og:*, twitter:*, canonical) into <head>. */}
          <HeadContent />
          <ScrollRestoration />
          <Outlet />
          <ToastViewport />
        </AuthModalProvider>
      </TooltipProvider>
    </LanguageProvider>
  );
}
